import {
  ApiAction,
  ApiContext,
  ApiItemType,
  ApiReturnFrontendPermission,
} from '@highimpact-frontend/api-types';
import { Typography } from '@mui/material';
import { forwardRef, useEffect, useState } from 'react';
import { api_permissions } from '../../../api/api-commons';

/**
 * Component props.
 */
type PermissionDecoratorV2Props = {
  item: ApiItemType;
  project_id?: number;
  site_id?: number;
  company_id?: number;
  children?: React.ReactNode;
  style?: React.CSSProperties;
};

// Fetcher function
const fetcher = async (
  url: string,
  params: ApiContext,
  signal: AbortSignal
) => {
  const res = await api_permissions.post(url, params, { signal });
  if (!res.data) throw new Error('Failed to fetch permissions');
  return res.data;
};

const PermissionDecorator = forwardRef<
  HTMLDivElement,
  PermissionDecoratorV2Props
>(
  (
    { children, item, project_id, site_id, company_id, style, ...props },
    ref
  ) => {
    const params: ApiContext = {
      project_id: project_id ? Number(project_id) : null,
      site_id: site_id ? Number(site_id) : null,
      company_id: company_id ? Number(company_id) : null,
      item,
    };

    /**
     * Data state handeling.
     */
    const [data, setData] = useState<ApiReturnFrontendPermission | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      let isMounted = true;
      const controller = new AbortController();
      setIsLoading(true);

      fetcher('/frontend-permissions', params, controller.signal)
        .then((res) => {
          if (isMounted) {
            setData(res);
            setError(null);
          }
        })
        .catch((err) => {
          const isCanceled = (err as Error)?.name === 'CanceledError';
          if (isMounted && !isCanceled) {
            setError(err);
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        });

      return () => {
        isMounted = false;
        controller.abort();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project_id, site_id, company_id, item]);
    // -----------------------------

    if (error || isLoading || !data) {
      return null;
    }

    const action: ApiAction = data['action'] ?? 'hide';

    if (action === 'hide') {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }

    if (action === 'hide_with_message') {
      return (
        <Typography>
          Vous n'avez pas les permissions nécessaires pour accéder à cette page
        </Typography>
      );
    }

    if (action === 'disable') {
      return (
        <div ref={ref} style={{ opacity: 0.5, pointerEvents: 'none' }}>
          {children}
        </div>
      );
    }

    return (
      <div ref={ref} {...props} style={style}>
        {children}
      </div>
    ); // display
  }
);

export default PermissionDecorator;
